import { Select, MenuItem, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { EvacZoneStatus, RegionEvacZoneStyle } from 'shared/types';
import {
  EVAC_ZONE_LABEL_TRANS_KEYS,
  EVAC_ZONE_STATUS_ENABLED,
  EvacZoneStyles,
} from '../../../../constants';
import { getEvacZoneStatusChipStyles } from '../../utils/getEvacZoneStatusChipStyles';

type EvacZoneLevelSelectProps = {
  value: string;
  internalStatus: string;
  evacZoneStyle: RegionEvacZoneStyle;
  disabled: boolean;
  onChange: (value: EvacZoneStatus) => void;
};

type EvacStatusBadgeProps = {
  color: string;
};

const EvacStatusBadge = ({ color }: EvacStatusBadgeProps): JSX.Element => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: color,
      mr: 1,
    }}
  />
);

export const EvacZoneLevelSelect = ({
  value,
  internalStatus,
  evacZoneStyle,
  disabled,
  onChange,
}: EvacZoneLevelSelectProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();
  const style = (evacZoneStyle ||
    EvacZoneStyles.default) as keyof typeof EVAC_ZONE_STATUS_ENABLED;

  const enabledStatuses = Object.keys(EVAC_ZONE_STATUS_ENABLED[style]).filter(
    (key) => EVAC_ZONE_STATUS_ENABLED[style][key as EvacZoneStatus] === true,
  ) as EvacZoneStatus[];

  // All onMouseDown handlers below prevent event propagation to the MUI DataGrid row,
  // allowing interaction with the Select without triggering row selection
  return (
    <Select
      size="small"
      value={value}
      displayEmpty
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as EvacZoneStatus)}
      onMouseDown={(e) => e.stopPropagation()}
      MenuProps={{
        onMouseDown: (e) => e.stopPropagation(),
      }}
      sx={{
        width: 110,
        bgcolor: theme.palette.background.paper,
        '& .MuiSelect-select': {
          padding: '4px 8px',
          fontSize: '0.875rem',
          color: theme.palette.text.primary,
        },
        '& .MuiSelect-icon': {
          color: theme.palette.text.secondary,
        },
      }}
    >
      <MenuItem
        key="status..."
        value={internalStatus || ''}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Typography color="#999">
          {t(`geoEventEvacuations.evacZones.table.statusSelection`)}
        </Typography>
      </MenuItem>
      {((!internalStatus && value) || internalStatus) && (
        <MenuItem
          key="normal"
          value=""
          onMouseDown={(e) => e.stopPropagation()}
        >
          <EvacStatusBadge
            color={
              getEvacZoneStatusChipStyles(theme, style, undefined)
                .indicatorColor
            }
          />
          {t(`geoEventEvacuations.evacZones.normal`)}
        </MenuItem>
      )}
      {enabledStatuses.map((status) => (
        <MenuItem
          key={status}
          value={status === internalStatus ? undefined : status}
          disabled={status === internalStatus}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <EvacStatusBadge
            color={
              getEvacZoneStatusChipStyles(theme, style, status).indicatorColor
            }
          />
          {t(`${EVAC_ZONE_LABEL_TRANS_KEYS[style][status]}Short`)}
        </MenuItem>
      ))}
    </Select>
  );
};
